import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { Folder, Unit } from '../../../../../../models'
import {
    Checkbox,
    makeStyles,
    Typography,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    ListItemText,
    Chip,
    IconButton,
    Tooltip,
} from '@material-ui/core'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import FolderIcon from '@material-ui/icons/Folder'
import MeetingRoomIcon from '@material-ui/icons/MeetingRoom'
import KingBedIcon from '@material-ui/icons/KingBed'
import BathtubIcon from '@material-ui/icons/Bathtub'
import KitchenIcon from '@material-ui/icons/Kitchen'
import InfoIcon from '@material-ui/icons/Info'
import { Container } from '../../../../../../components'
import { useTheme } from '@material-ui/core/styles'
import { SelectedState } from './CreateInspectionCard'
import { typographyStyle } from '../../../../../../theme/typographyStyle'

const useStyles = makeStyles((theme) => ({
    treeContainer: {
        width: '100%',
        maxHeight: 500,
        overflowY: 'auto',
        padding: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
    },
    nodeItem: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0.75, 1),
        cursor: 'pointer',
        borderRadius: theme.shape.borderRadius,
        marginBottom: 2,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    nodeContent: {
        display: 'flex',
        alignItems: 'center',
        flex: 1,
    },
    childrenContainer: {
        marginLeft: theme.spacing(4),
        borderLeft: `2px solid ${theme.palette.divider}`,
        paddingLeft: theme.spacing(1),
    },
    areaContainer: {
        marginLeft: theme.spacing(2),
        borderLeft: `1px solid ${theme.palette.grey[300]}`,
        paddingLeft: theme.spacing(1),
        marginTop: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5),
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.shape.borderRadius,
    },
    selected: {
        backgroundColor: `${theme.palette.primary.light}20`,
    },
    checkIcon: {
        color: theme.palette.success.main,
        marginLeft: theme.spacing(1),
    },
    folderIcon: {
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1),
    },
    fileIcon: {
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1),
    },
    areaIcon: {
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1),
        fontSize: '0.9rem',
        width: 20,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    bulletIcon: {
        color: theme.palette.text.disabled,
        marginRight: theme.spacing(1),
        fontSize: '0.9rem',
        width: 20,
        display: 'flex',
        justifyContent: 'center',
    },
    arrowIcon: {
        cursor: 'pointer',
        marginRight: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
    arrowPlaceholder: {
        width: 24,
        marginRight: theme.spacing(1),
    },
    nodeLabel: {
        fontWeight: 500,
    },
    nodeNumber: {
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1),
        backgroundColor: theme.palette.grey[100],
        padding: theme.spacing(0.25, 0.5),
        borderRadius: 4,
        fontSize: '0.75rem',
        minWidth: 28,
        textAlign: 'center',
        display: 'inline-block',
    },
    areaLabel: {
        color: theme.palette.text.secondary,
        fontSize: '0.85rem',
    },
    areaItem: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0.5, 1),
        cursor: 'pointer',
        borderRadius: theme.shape.borderRadius,
        marginBottom: 1,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    unitContent: {
        marginBottom: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
    },
    formControl: {
        marginBottom: theme.spacing(2),
        width: '100%',
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    chip: {
        margin: 2,
    },
    tooltipText: {
        fontSize: '16px',
    },
}))

interface Props {
    root: Folder
    onSelectionChange: (selected: SelectedState) => void
    initialSelected: SelectedState
}

// Define Area interface based on the model
interface Area {
    id: number
    area_label: string
    area_config: number
    unit: number
    area_config_name?: string
}

// Define a new interface for area combinations
interface AreaCombination {
    id: string // A unique identifier for the combination
    configName: string
    label: string
    displayName: string // The combined display name
}

export const AssignInspection = (props: Props) => {
    const { root, onSelectionChange, initialSelected } = props
    const classes = useStyles()
    const theme = useTheme()

    // Local state for UI-only concerns
    const [expandedFolders, setExpandedFolders] = useState<
        Record<number, boolean>
    >({})
    const [expandedUnits, setExpandedUnits] = useState<Record<number, boolean>>(
        {},
    )
    const [selected, setSelected] = useState<SelectedState>(initialSelected)

    // Get unique area combinations from selected units
    const uniqueAreaCombinations = useMemo<AreaCombination[]>(() => {
        if (!root) return []

        const combinationsMap = new Map<string, AreaCombination>()

        // Helper function to collect unique combinations
        const collectCombinations = (folder: Folder) => {
            // Process units in this folder if they're selected
            folder.units.forEach((unit) => {
                if (selected.units[unit.id] && unit.areas) {
                    unit.areas.forEach((area) => {
                        if (area.area_config_name) {
                            const configName =
                                area.area_config_name || 'Unknown'
                            const label = area.area_label || ''
                            const combinationId = `${configName}-${label}`

                            if (!combinationsMap.has(combinationId)) {
                                combinationsMap.set(combinationId, {
                                    id: combinationId,
                                    configName,
                                    label,
                                    displayName: `${configName} - ${label}`,
                                })
                            }
                        }
                    })
                }
            })

            // Process child folders recursively
            folder.children.forEach(collectCombinations)
        }

        collectCombinations(root)

        return Array.from(combinationsMap.values()).sort((a, b) =>
            a.displayName.localeCompare(b.displayName),
        )
    }, [root, selected.units])

    // By default, all area combinations should be selected
    const [selectedAreaCombinations, setSelectedAreaCombinations] = useState<
        string[]
    >([])

    // Initialize selectedAreaCombinations with all available combinations
    useEffect(() => {
        if (
            uniqueAreaCombinations.length > 0 &&
            selectedAreaCombinations.length === 0
        ) {
            // Only set if we have combinations and haven't initialized yet
            setSelectedAreaCombinations(
                uniqueAreaCombinations.map((combo) => combo.id),
            )
        }
    }, [uniqueAreaCombinations, selectedAreaCombinations.length])

    // Get count of selected items
    const selectedCount = useMemo(() => {
        return {
            folders: Object.keys(selected.folders).length,
            units: Object.keys(selected.units).length,
            areas: Object.keys(selected.areas).length,
        }
    }, [selected])

    // Check if an item is selected
    const isSelected = useCallback(
        (type: 'folder' | 'unit' | 'area', id: number): boolean => {
            if (type === 'folder') {
                return !!selected.folders[id]
            } else if (type === 'unit') {
                return !!selected.units[id]
            } else {
                return !!selected.areas[id]
            }
        },
        [selected],
    )

    // Check if an area should be selected based on combinations
    const shouldAreaBeSelected = useCallback(
        (area: Area): boolean => {
            if (!area.area_config_name || !area.area_label) return true

            // If no combinations are selected, don't filter anything
            if (selectedAreaCombinations.length === 0) return true

            const combinationId = `${area.area_config_name}-${area.area_label}`
            return selectedAreaCombinations.includes(combinationId)
        },
        [selectedAreaCombinations],
    )

    // Get appropriate icon for an area
    const getAreaIcon = useCallback(
        (area: any) => {
            if (area.area_config_name === 'Bed') {
                return (
                    <KingBedIcon
                        className={classes.areaIcon}
                        fontSize="small"
                    />
                )
            } else if (area.area_config_name === 'Bath') {
                return (
                    <BathtubIcon
                        className={classes.areaIcon}
                        fontSize="small"
                    />
                )
            } else if (area.area_config_name === 'Com') {
                return (
                    <KitchenIcon
                        className={classes.areaIcon}
                        fontSize="small"
                    />
                )
            }
            return <div className={classes.bulletIcon}>•</div>
        },
        [classes],
    )

    // Update parent component with selection changes
    const updateSelection = useCallback(
        (newSelection: SelectedState) => {
            // Update local state
            setSelected(newSelection)
            // Notify parent
            onSelectionChange(newSelection)
        },
        [onSelectionChange],
    )

    // Recursively select/deselect a folder and its children
    const selectAllInFolder = useCallback(
        (folder: Folder, isSelected: boolean): SelectedState => {
            const updates: SelectedState = {
                folders: { ...selected.folders },
                units: { ...selected.units },
                areas: { ...selected.areas },
            }

            // Select/deselect this folder
            if (isSelected) {
                updates.folders[folder.id] = true
            } else {
                delete updates.folders[folder.id]
            }

            // Helper recursive function to process folder hierarchy
            const processFolder = (currentFolder: Folder) => {
                // Process this folder's units
                currentFolder.units.forEach((unit) => {
                    if (isSelected) {
                        updates.units[unit.id] = true
                    } else {
                        delete updates.units[unit.id]
                    }

                    // Process areas
                    if (unit.areas) {
                        unit.areas.forEach((area) => {
                            if (isSelected) {
                                updates.areas[area.id] = true
                            } else {
                                delete updates.areas[area.id]
                            }
                        })
                    }
                })

                // Process child folders recursively
                currentFolder.children.forEach((childFolder) => {
                    if (isSelected) {
                        updates.folders[childFolder.id] = true
                    } else {
                        delete updates.folders[childFolder.id]
                    }
                    processFolder(childFolder)
                })
            }

            processFolder(folder)

            return updates
        },
        [selected],
    )

    // Select/deselect a unit and its areas
    const selectUnit = useCallback(
        (unit: Unit, isSelected: boolean): SelectedState => {
            const updates: SelectedState = {
                folders: { ...selected.folders },
                units: { ...selected.units },
                areas: { ...selected.areas },
            }

            // Select/deselect this unit
            if (isSelected) {
                updates.units[unit.id] = true
            } else {
                delete updates.units[unit.id]
            }

            // Process areas
            if (unit.areas) {
                unit.areas.forEach((area) => {
                    if (isSelected) {
                        updates.areas[area.id] = true
                    } else {
                        delete updates.areas[area.id]
                    }
                })
            }

            return updates
        },
        [selected],
    )

    // Toggle folder expansion
    const handleToggleFolderExpand = useCallback(
        (folderId: number, e: React.MouseEvent) => {
            e.stopPropagation()
            const newExpanded = !expandedFolders[folderId]
            setExpandedFolders((prev) => ({
                ...prev,
                [folderId]: newExpanded,
            }))
        },
        [expandedFolders],
    )

    // Toggle unit expansion
    const handleToggleUnitExpand = useCallback(
        (unitId: number, e: React.MouseEvent) => {
            e.stopPropagation()
            const newExpanded = !expandedUnits[unitId]
            setExpandedUnits((prev) => ({
                ...prev,
                [unitId]: newExpanded,
            }))
        },
        [expandedUnits],
    )

    // Handle selection toggle for folders, units, and areas
    const handleToggleSelect = useCallback(
        (type: 'folder' | 'unit' | 'area', item: any, e: React.MouseEvent) => {
            e.stopPropagation()

            let updates: SelectedState
            const itemSelected = isSelected(type, item.id)
            const newState = !itemSelected

            if (type === 'folder') {
                updates = selectAllInFolder(item, newState)
            } else if (type === 'unit') {
                updates = selectUnit(item, newState)
            } else {
                // Just select/deselect this area
                updates = {
                    ...selected,
                    areas: { ...selected.areas },
                }

                if (newState) {
                    updates.areas[item.id] = true
                } else {
                    delete updates.areas[item.id]
                }
            }

            // Update selection
            updateSelection(updates)
        },
        [isSelected, selectAllInFolder, selectUnit, selected, updateSelection],
    )

    // Handle dropdown selection change
    const handleAreaCombinationChange = useCallback(
        (event: React.ChangeEvent<{ value: unknown }>) => {
            const newSelectedCombinations = event.target.value as string[]
            const previousSelectedCombinations = selectedAreaCombinations
            setSelectedAreaCombinations(newSelectedCombinations)

            // Find newly added combinations (ones that were just selected)
            const newlyAddedCombinations = newSelectedCombinations.filter(
                (combo) => !previousSelectedCombinations.includes(combo),
            )

            // Create a new selection state
            const updates: SelectedState = {
                folders: { ...selected.folders },
                units: { ...selected.units },
                areas: { ...selected.areas }, // Keep existing area selections
            }

            // Helper function to process areas based on combinations
            const processFolder = (folder: Folder) => {
                // Process units in this folder if they're selected
                folder.units.forEach((unit) => {
                    if (updates.units[unit.id] && unit.areas) {
                        unit.areas.forEach((area) => {
                            if (!area.area_config_name || !area.area_label)
                                return

                            const combinationId = `${area.area_config_name}-${area.area_label}`

                            // If the area matches a newly added combination, select it
                            if (
                                newlyAddedCombinations.includes(combinationId)
                            ) {
                                updates.areas[area.id] = true
                            }

                            // If the area was in the previous selection but is no longer selected,
                            // deselect it
                            if (
                                !newSelectedCombinations.includes(
                                    combinationId,
                                ) &&
                                previousSelectedCombinations.includes(
                                    combinationId,
                                )
                            ) {
                                delete updates.areas[area.id]
                            }
                        })
                    }
                })

                // Process child folders recursively
                folder.children.forEach(processFolder)
            }

            // Start processing from the root
            processFolder(root)

            // If all combinations are selected, ensure all areas in selected units are selected
            if (
                newSelectedCombinations.length === uniqueAreaCombinations.length
            ) {
                // Reselect all areas for selected units
                const selectAllAreas = (folder: Folder) => {
                    folder.units.forEach((unit) => {
                        if (updates.units[unit.id] && unit.areas) {
                            unit.areas.forEach((area) => {
                                updates.areas[area.id] = true
                            })
                        }
                    })
                    folder.children.forEach(selectAllAreas)
                }

                selectAllAreas(root)
            }

            // Update the selection
            updateSelection(updates)
        },
        [
            root,
            selected,
            updateSelection,
            selectedAreaCombinations,
            uniqueAreaCombinations,
        ],
    )

    // Render a unit and its areas
    const renderUnit = useCallback(
        (unit: Unit, level: number): JSX.Element => {
            const isExpanded = !!expandedUnits[unit.id]
            const unitSelected = isSelected('unit', unit.id)

            const allAreas = unit.areas || []
            const hasAreas = allAreas.length > 0

            return (
                <div key={`unit-${unit.id}`}>
                    <div
                        className={`${classes.nodeItem} ${
                            unitSelected ? classes.selected : ''
                        }`}
                        onClick={(e) => {
                            e.stopPropagation()
                            if (hasAreas) {
                                handleToggleUnitExpand(unit.id, e)
                            }
                        }}
                    >
                        <div className={classes.nodeContent}>
                            {hasAreas ? (
                                <div
                                    className={classes.arrowIcon}
                                    onClick={(e) =>
                                        handleToggleUnitExpand(unit.id, e)
                                    }
                                >
                                    {isExpanded ? (
                                        <KeyboardArrowDownIcon fontSize="small" />
                                    ) : (
                                        <KeyboardArrowRightIcon fontSize="small" />
                                    )}
                                </div>
                            ) : (
                                <div className={classes.arrowPlaceholder} />
                            )}

                            <MeetingRoomIcon
                                className={classes.fileIcon}
                                fontSize="small"
                            />

                            <Typography
                                variant="body2"
                                className={classes.nodeLabel}
                            >
                                {unit.name}
                            </Typography>
                        </div>

                        <Checkbox
                            checked={unitSelected}
                            onClick={(e) => {
                                e.stopPropagation()
                                handleToggleSelect('unit', unit, e)
                            }}
                            color="primary"
                            size="small"
                        />
                    </div>

                    {isExpanded && hasAreas && (
                        <div className={classes.unitContent}>
                            <div className={classes.areaContainer}>
                                {allAreas.map((area) => {
                                    const areaSelected = isSelected(
                                        'area',
                                        area.id,
                                    )
                                    const shouldBeSelected = shouldAreaBeSelected(
                                        area,
                                    )

                                    // CHANGED: Don't hide areas, just show them differently
                                    // if (selectedAreaCombinations.length > 0 && !shouldBeSelected) {
                                    //     return null
                                    // }

                                    return (
                                        <div
                                            key={`area-${area.id}`}
                                            className={`${classes.areaItem} ${
                                                areaSelected
                                                    ? classes.selected
                                                    : ''
                                            }`}
                                            onClick={(e) =>
                                                handleToggleSelect(
                                                    'area',
                                                    area,
                                                    e,
                                                )
                                            }
                                            style={
                                                selectedAreaCombinations.length >
                                                    0 && !shouldBeSelected
                                                    ? { opacity: 0.5 } // Make filtered-out areas appear dimmed
                                                    : {}
                                            }
                                        >
                                            <div
                                                className={classes.nodeContent}
                                            >
                                                {getAreaIcon(area)}

                                                <Typography
                                                    variant="body2"
                                                    className={
                                                        classes.areaLabel
                                                    }
                                                >
                                                    {area.area_config_name} -{' '}
                                                    {area.area_label}
                                                </Typography>
                                            </div>

                                            <Checkbox
                                                checked={areaSelected}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    handleToggleSelect(
                                                        'area',
                                                        area,
                                                        e,
                                                    )
                                                }}
                                                color="primary"
                                                size="small"
                                                // ADDED: If area is filtered out by dropdown, disable the checkbox
                                                disabled={
                                                    selectedAreaCombinations.length >
                                                        0 && !shouldBeSelected
                                                }
                                            />
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )
        },
        [
            classes,
            expandedUnits,
            isSelected,
            handleToggleUnitExpand,
            handleToggleSelect,
            getAreaIcon,
            shouldAreaBeSelected,
            selectedAreaCombinations,
        ],
    )

    // Render a folder and its children
    const renderFolder = useCallback(
        (folder: Folder, level: number = 0): JSX.Element => {
            const isExpanded = !!expandedFolders[folder.id]
            const folderSelected = isSelected('folder', folder.id)

            const hasChildren =
                folder.children.length > 0 || folder.units.length > 0

            return (
                <div key={`folder-${folder.id}`}>
                    <div
                        className={`${classes.nodeItem} ${
                            folderSelected ? classes.selected : ''
                        }`}
                        onClick={(e) => {
                            e.stopPropagation()
                            if (hasChildren) {
                                handleToggleFolderExpand(folder.id, e)
                            }
                        }}
                    >
                        <div className={classes.nodeContent}>
                            {hasChildren ? (
                                <div
                                    className={classes.arrowIcon}
                                    onClick={(e) =>
                                        handleToggleFolderExpand(folder.id, e)
                                    }
                                >
                                    {isExpanded ? (
                                        <KeyboardArrowDownIcon fontSize="small" />
                                    ) : (
                                        <KeyboardArrowRightIcon fontSize="small" />
                                    )}
                                </div>
                            ) : (
                                <div className={classes.arrowPlaceholder} />
                            )}

                            <FolderIcon
                                className={classes.folderIcon}
                                fontSize="small"
                            />

                            <Typography
                                variant="body2"
                                className={classes.nodeLabel}
                            >
                                {folder.name}
                            </Typography>
                        </div>

                        <Checkbox
                            checked={folderSelected}
                            onClick={(e) => {
                                e.stopPropagation()
                                handleToggleSelect('folder', folder, e)
                            }}
                            color="primary"
                            size="small"
                        />
                    </div>

                    {isExpanded && (
                        <div className={classes.childrenContainer}>
                            {/* Render child folders */}
                            {folder.children.map((childFolder) =>
                                renderFolder(childFolder, level + 1),
                            )}

                            {/* Render units */}
                            {folder.units.map((unit) =>
                                renderUnit(unit, level + 1),
                            )}
                        </div>
                    )}
                </div>
            )
        },
        [
            classes,
            expandedFolders,
            isSelected,
            handleToggleFolderExpand,
            handleToggleSelect,
            renderUnit,
        ],
    )

    const infoMessage = `Select the units that you would like to include in this inspection.
    By default, all areas in the selected units will be inspected. If you would like to remove
    a specific area from all of the units for this inspection, you can do so by deselecting
    the area in the dropdown below.`

    return (
        <Container
            direction="column"
            style={{
                width: '100%',
                marginTop: theme.spacing(1),
                marginBottom: theme.spacing(1),
            }}
        >
            <Container direction="row">
                <Typography
                    style={{
                        ...typographyStyle(theme.typography.h6),
                        fontWeight: 'bold',
                        marginBottom: theme.spacing(1),
                    }}
                >
                    Assign Inspection
                </Typography>
                <Container style={{ marginBottom: theme.spacing(1) }}>
                    <Tooltip
                        title={infoMessage}
                        classes={{ tooltip: classes.tooltipText }}
                    >
                        <IconButton size="small">
                            <InfoIcon fontSize="inherit" />
                        </IconButton>
                    </Tooltip>
                </Container>
            </Container>

            {/* Selected count */}
            <Container style={{ marginBottom: theme.spacing(2) }}>
                <Typography variant="body2" style={{ marginTop: 8 }}>
                    Selected: {selectedCount.folders} folders,{' '}
                    {selectedCount.units} units, {selectedCount.areas} areas
                </Typography>
            </Container>

            {/* Area combination filter dropdown */}
            <FormControl
                className={classes.formControl}
                variant="outlined"
                size="small"
            >
                <InputLabel id="area-combination-select-label">
                    Assign to Areas
                </InputLabel>
                <Select
                    labelId="area-combination-select-label"
                    id="area-combination-select"
                    multiple
                    value={selectedAreaCombinations}
                    onChange={handleAreaCombinationChange}
                    renderValue={(selected) => {
                        const selectedValues = selected as string[]

                        // If no combinations available
                        if (uniqueAreaCombinations.length === 0) {
                            return <em>No areas available</em>
                        }

                        // Check if all combinations are selected
                        const allSelected =
                            uniqueAreaCombinations.length > 0 &&
                            selectedValues.length ===
                                uniqueAreaCombinations.length

                        if (allSelected) {
                            return <strong>All Areas</strong>
                        }

                        // If some are deselected, show deselected ones in red
                        const deselectedCombinations = uniqueAreaCombinations.filter(
                            (combo) => !selectedValues.includes(combo.id),
                        )

                        return (
                            <div className={classes.chips}>
                                {deselectedCombinations.length > 0 ? (
                                    deselectedCombinations.map((combo) => (
                                        <Chip
                                            key={combo.id}
                                            label={combo.displayName}
                                            className={classes.chip}
                                            size="small"
                                            style={{
                                                backgroundColor:
                                                    theme.palette.error.dark,
                                                color: theme.palette.getContrastText(
                                                    theme.palette.error.dark,
                                                ),
                                            }}
                                        />
                                    ))
                                ) : (
                                    <em>All Areas Selected</em>
                                )}
                            </div>
                        )
                    }}
                    label="Assign to Areas"
                    disabled={uniqueAreaCombinations.length === 0}
                >
                    {uniqueAreaCombinations.length > 0 ? (
                        uniqueAreaCombinations.map((combo) => (
                            <MenuItem key={combo.id} value={combo.id}>
                                <Checkbox
                                    checked={
                                        selectedAreaCombinations.indexOf(
                                            combo.id,
                                        ) > -1
                                    }
                                />
                                <ListItemText primary={combo.displayName} />
                            </MenuItem>
                        ))
                    ) : (
                        <MenuItem disabled>
                            <ListItemText primary="Select units to see available areas" />
                        </MenuItem>
                    )}
                </Select>
            </FormControl>

            <Paper className={classes.treeContainer} elevation={0}>
                {root && renderFolder(root, 0)}
            </Paper>
        </Container>
    )
}
